// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getProductIdsForCompare,
    getProductsForCompare,
    MAX_PRODUCTS_TO_COMPARE,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import loadProductIdsToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/LoadProductIdsToCompare";
import loadProductsToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/LoadProductsToCompare";
import removeAllProductsFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveAllProductsFromCompare";
import removeProductIdToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import setProductCompareReturnUrl from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/SetProductCompareReturnUrl";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { CarouselSlide, CarouselStyles } from "@insite/content-library/Components/Carousel";
import ProductCarouselProductCondensed from "@insite/content-library/Components/ProductCarouselProductCondensed";
import Button, { ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps, PageProps } from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { xGoogleAnalyticsState } from "@werner/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@werner/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import Close from "@werner/XDL/Icons/Close";
import { color, mergeWithCss, xScreen } from "@werner/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const PRODUCT_COMPARE_CAROUSEL_ID = "product-compare-carousel-id";

interface OwnProps {
    extendedStyles?: ProductCompareFlyOutStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapStateToProps = (state: CustomAppState) => ({
    productIds: getProductIdsForCompare(state),
    products: getProductsForCompare(state),
    productComparePageLink: getPageLinkByPageType(state, "ProductComparePage"),
    currentLocation: getLocation(state),
    isTabletScreen: state.xCommon.isTabletScreen,
    xGoogleAnalytics: (state as unknown as xGoogleAnalyticsState).xGoogleAnalytics,
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    loadProductIdsToCompare,
    loadProductsToCompare,
    removeProductIdToCompare,
    removeAllProductsFromCompare,
    setProductCompareReturnUrl,
};

export interface ProductCompareFlyOutStyles {
    carousel?: CarouselStyles;
    carouselDrawer?: DrawerPresentationProps;
    carouselDrawerContainer?: InjectableCss;
    removeProductFromCompareButton?: ButtonPresentationProps;
    removeProductFromCompareButtonIcon?: ButtonIconPresentationProps;
    headerGridContainer?: GridContainerProps;
    headerTitleGridItem?: GridItemProps;
    headerTitleText?: TypographyPresentationProps;
    headerActionsGridItem?: GridItemProps;
    headerCompareButton?: ButtonPresentationProps;
    // XNG-Changes
    carouselDrawerPage?: MobiusPageProps;
    headerRemoveAllButton?: ButtonPresentationProps;
    compareHeaderStyles?: {
        titleText?: TypographyPresentationProps;
        container?: PageProps;
        toggle?: ClickableProps;
        icon?: IconPresentationProps;
    };
    actionsWrapper?: InjectableCss;
    slidesWrapper?: InjectableCss;
    slideItem?: InjectableCss;
}

export const productCompareFlyOutStyles: ProductCompareFlyOutStyles = {
    carouselDrawer: {
        position: "bottom",
        cssOverrides: {
            scrim: css`
                transition: 300ms ease-in-out;
                background: transparent;
            `,
            drawerTitle: css`
                display: none;
            `,
            drawerBody: css`
                background: linear-gradient(
                    to bottom,
                    transparent 0%,
                    transparent 40%,
                    ${color("common.white", 1)} 40%,
                    ${color("common.white", 1)} 100%
                );
                box-shadow: none;
            `,
            drawerContent: css`
                background-color: transparent;
                overflow-y: hidden;
                ${xScreen("lte", "xs")} {
                    height: 100%;
                }
            `,
        },
        closeButtonProps: {
            css: css`
                display: none;
            `,
        },
        enableClickThrough: true,
    },

    // XNG-Change
    carouselDrawerContainer: {
        css: css`
            padding: 25px 0;
            background: ${color("common.white")};
            box-shadow: 0 0 15px 0 ${color("primary.main", 0.15)};
            ${xScreen("lte", "xs")} {
                height: 100%;
            }
        `,
    },
    carouselDrawerPage: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: center;
            &&& {
                padding: 0;
                background: inherit;
            }
        `,
    },
    removeProductFromCompareButton: {
        css: css`
            position: absolute;
            right: 4px;
            top: 4px;
            padding: 8px;
            &:hover {
                ${IconWrapper} {
                    color: ${color("text.main")};
                }
            }
        `,
    },
    // XNG-Change
    headerRemoveAllButton: {
        variant: "secondary",
        typographyProps: {
            css: css`
                text-align: center;
                width: 100%;
            `,
        },
        css: css`
            width: 100%;
            min-height: 46px;
            margin-bottom: 12px;
            ${xScreen("lte", "md")} {
                width: 100%;
                min-width: unset;
            }
        `,
    },
    // XNG-Change
    headerCompareButton: {
        css: css`
            margin-bottom: 12px;
            ${xScreen("lte", "md")} {
                width: 100%;
                min-width: unset;
            }
        `,
    },
    compareHeaderStyles: {
        container: {
            css: css`
                &&& {
                    padding: 0 20px;
                }
                height: 36px;
                background-color: transparent;
            `,
        },
        titleText: {
            size: 16,
            weight: "bold",
            color: "common.white",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
            `,
        },
        toggle: {
            css: css`
                display: flex;
                gap: 10px;
                align-items: center;
                justify-content: center;
                padding: 6px 25px;
                background: ${color("primary")};
                border-radius: 3px 3px 0 0;
                &:focus {
                    outline: none;
                }
            `,
        },
        icon: {
            size: 24,
            color: "common.white",
        },
    },
    // XNG-Change
    actionsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 0 40px;
            justify-content: flex-start;
            ${xScreen("lte", "md")} {
                padding: 0 10px;
            }
            ${xScreen("lte", 767)} {
                padding: 10px;
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: row;
                justify-content: center;
                overflow: hidden;
                gap: 15px;
            }
        `,
    },
    slidesWrapper: {
        css: css`
            display: none;
            width: 100%;
            padding-left: 10px;
            ${xScreen("gte", "md")} {
                display: flex;
                gap: 14px;
                justify-content: center;
            }
        `,
    },
    slideItem: {
        css: css`
            width: 120px;
            height: 120px;
            scroll-snap-align: start;
            ${xScreen("lte", "md")} {
                width: 92px;
                height: 92px;
            }
        `,
    },
};

const mapProductsToSlides = (
    products: ProductModel[],
    styles: ProductCompareFlyOutStyles,
    removeProductIdToCompare: (productId: string) => void,
) =>
    products.map(product => ({
        id: product.id,
        renderComponent: function ProductCarouselProductCondensedRenderer() {
            return (
                <ProductCarouselProductCondensed
                    carouselId={PRODUCT_COMPARE_CAROUSEL_ID}
                    product={product}
                    labelsVisible={false}
                    showImage={true}
                    showBrand={false}
                    showTitle={false}
                    showPartNumbers={false}
                    showPrice={false}
                    showAddToCart={false}
                    showAddToList={false}
                    condensedActionsTemplate={(product: ProductModel) => {
                        return (
                            <Clickable
                                {...styles.removeProductFromCompareButton}
                                onClick={() => removeProductIdToCompare(product.id)}
                                data-test-selector="productCompareRemoveProductButton"
                                data-test-key={product.id}
                                aria-label="Close"
                            >
                                <VisuallyHidden>
                                    {translate("Remove Product {0} from Compare", product.productTitle)}
                                </VisuallyHidden>
                                <Icon src={Close} size={10} color="common.ui3" />
                            </Clickable>
                        );
                    }}
                />
            );
        },
    }));

const ProductCompareFlyOut = ({
    extendedStyles,
    history,
    productIds,
    products,
    productComparePageLink,
    currentLocation,
    loadProductIdsToCompare,
    loadProductsToCompare,
    removeProductIdToCompare,
    removeAllProductsFromCompare,
    setProductCompareReturnUrl,
    isTabletScreen,
    xGoogleAnalytics,
    isAuthenticated,
}: Props) => {
    const [styles] = useState(() => mergeWithCss(productCompareFlyOutStyles, extendedStyles));

    const [slides, setSlides] = useState<CarouselSlide[]>([]);
    // Xng-changes
    const [isOpen, setIsOpen] = useState<boolean>(true);

    useEffect(() => {
        // We load in the ProductIds to Compare from storage
        loadProductIdsToCompare();
    }, []);

    useEffect(() => {
        // Load in products from the ProductCompareService Compare Storage
        if (productIds.length === 0) {
            return;
        }

        loadProductsToCompare({ id: PRODUCT_COMPARE_CAROUSEL_ID, productIds, includeAttributeTypes: true });
    }, [productIds]);

    useEffect(() => {
        if (products.length === 0) {
            return;
        }
        setSlides(mapProductsToSlides(products, styles, productId => removeProductIdToCompare({ productId })));
    }, [products, styles]);

    const handleRemoveAll = () => {
        removeAllProductsFromCompare();
    };
    const handleOpenCompare = () => {
        if (productComparePageLink) {
            sendTracking();
            setProductCompareReturnUrl({ returnUrl: currentLocation.pathname + currentLocation.search });
            history.push(productComparePageLink.url);
        }
    };

    // Xng-Change
    // WES-1780
    const sendTracking = () => {
        const dataLayer = getDataLayer();

        if (!dataLayer || !xGoogleAnalytics.compareProductsEvent) {
            return;
        }

        const googleAnalyticsData = {
            event: xGoogleAnalytics.compareProductsEventName,
            click_text: translate("compare"),
            destination: productComparePageLink?.url,
            affiliation: xGoogleAnalytics.affiliation,
            authentication_state: isAuthenticated ? "authenticated" : "unauthenticated",
            items: products.map(p => {
                return p.productNumber;
            }),
        };
        dataLayer.push(googleAnalyticsData);
    };

    if (products.length === 0) {
        return null;
    }

    // XNG-Changes: update structure
    return (
        <Drawer
            draggable={false}
            isOpen={true}
            // XNG-Change: size value
            size={isOpen ? (isTabletScreen ? 180 : 230) : 36}
            {...styles.carouselDrawer}
            data-test-selector="productCompareDrawer"
        >
            <ProductCompareHeader
                isOpen={isOpen}
                onFlyOutToggle={() => setIsOpen(prevState => !prevState)}
                styles={styles}
                productCount={products.length}
                onOpenCompare={handleOpenCompare}
                onRemoveAll={handleRemoveAll}
            />
            <StyledWrapper {...styles.carouselDrawerContainer}>
                <Page {...styles.carouselDrawerPage}>
                    <StyledWrapper {...styles.slidesWrapper}>
                        {slides.map(slide => {
                            return (
                                <StyledWrapper key={slide.id} {...styles.slideItem}>
                                    {slide.renderComponent({})}
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                    <StyledWrapper {...styles.actionsWrapper}>
                        <Button
                            {...styles.headerCompareButton}
                            onClick={handleOpenCompare}
                            data-test-selector="productCompareOpenComparePageButton"
                            aria-label="Compare"
                        >
                            {translate("Compare")}
                        </Button>
                        <Button
                            {...styles.headerRemoveAllButton}
                            onClick={handleRemoveAll}
                            data-test-selector="productCompareRemoveAllButton"
                            aria-label="Clear All"
                        >
                            {translate("Clear All")}
                        </Button>
                    </StyledWrapper>
                </Page>
            </StyledWrapper>
        </Drawer>
    );
};

interface ProductCompareHeaderProps {
    styles: ProductCompareFlyOutStyles;
    productCount: number;
    onOpenCompare: () => void;
    onRemoveAll: () => void;
    // XNG-Changes
    isOpen: boolean;
    onFlyOutToggle: () => void;
}

const ProductCompareHeader = ({
    styles,
    productCount,
    onOpenCompare,
    onRemoveAll,
    isOpen,
    onFlyOutToggle,
}: ProductCompareHeaderProps) => {
    return (
        <Page {...styles.compareHeaderStyles?.container}>
            <Clickable
                {...styles.compareHeaderStyles?.toggle}
                onClick={onFlyOutToggle}
                aria-label={`${translate(`Compare {0} of ${MAX_PRODUCTS_TO_COMPARE} Items`, productCount.toString())}`}
            >
                <Icon src={isOpen ? ChevronDown : ChevronUp} {...styles.compareHeaderStyles?.icon} />
                <Typography {...styles.compareHeaderStyles?.titleText}>
                    {translate(`Compare {0} of ${MAX_PRODUCTS_TO_COMPARE} Items`, productCount.toString())}
                </Typography>
            </Clickable>
        </Page>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(ProductCompareFlyOut));
